<template>
  <div>
    <div class="d-flex flex-column row-gap-1">
      <div class="d-flex align-center column-gap-2">
        <!-- Title -->
        <h3 class="text--medium text--bold" :class="{ 'text-grey-400': isExpiredPack }">
          {{ packTitle }}
          <AppInfoTooltip>
            <p class="font-weight-semi-bold ">{{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.details') }}</p>
            <p>
              {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.subscriptionDate') }}
              {{ ISOToDDMMYYYY(pack.from) }}
            </p>
            <p v-if="packDuration.years || packDuration.months">
              {{ $t('subscriptions.views.SubscriptionConsumption.Packs.validityDuration.title') }}
              <i18n-t v-if="packDuration.years"
                      keypath="subscriptions.views.SubscriptionConsumption.Packs.validityDuration.years"
                      :plural="packDuration.years"
                      tag="span"
                      scope="global"
                      class="mr-1"
              >
                <template #duration>
                  {{ packDuration.years }}
                </template>
              </i18n-t>
              <i18n-t v-if="packDuration.months"
                      keypath="subscriptions.views.SubscriptionConsumption.Packs.validityDuration.months"
                      :plural="packDuration.months"
                      tag="span"
                      scope="global"
              >
                <template #duration>
                  {{ packDuration.months }}
                </template>
              </i18n-t>
            </p>
            <p>
              {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.endDate') }}
              {{ ISOToDDMMYYYY(pack.to) }}
            </p>
          </AppInfoTooltip>
        </h3>

        <!-- Expired -->
        <v-chip v-if="isExpiredPack" 
          label
          density="compact"
          class="text--extra-bold text-grey-700 px-2 d-flex align-center no-shrink"
        >
          {{ $t('subscriptions.views.SubscriptionConsumption.Packs.expired') }}
        </v-chip>

        <!-- Share Button -->
        <app-button 
          class="ml-auto"
          type="icon"
          size="small"
          @click="openShareSubscriptionConsumptionDialog()"
        >
          <app-material-icon name="share" />
        </app-button>
      </div>
      <!-- Subtitle -->
      <p v-if="!isExpiredPack" class="mb-0 text--x-small text-grey-700">
        {{ $t('subscriptions.views.SubscriptionConsumption.Packs.subTitle') }}
      </p>
    </div>

    <div class="mt-6">
      <div class="d-flex align-center column-gap-1 mb-1">
        <div :class=" isExpiredPack ? 'text-grey-400' : 'text-grey-700' ">
          {{ pack.consumed }}
          <i18n-t :keypath="`subscriptions.views.SubscriptionConsumption.Packs.${isSignaturePack ? 'signaturesUsed' : 'envelopesUsed'}`"
                  tag="span"
                  :plural="pack.consumed"
          />
          {{ pack.total }}
        </div>
      </div>

      <v-progress-linear :model-value="progressBarValue"
                         :color="DATA_VISUALISATION_COLORS[isExpiredPack ? 'Grey-200' : 'Purple-200']"
                         bg-color="grey-300"
                         height="20"
                         rounded>
      </v-progress-linear>
    </div>

    <!-- DIALOGS -->
    <ShareSubscriptionConsumptionDialog v-if="shareSubscriptionConsumptionDialogIsOpen"
                                        :title="shareSubscriptionConsumptionDialogTitle"
                                        :submit-action="SHARE_SUBSCRIPTION_CONSUMPTION_PACK"
                                        :action-params="{ packId: pack.id }"
                                        :ok-loading="shareSubscriptionConsumptionPackPending"
                                        @close="closeShareSubscriptionConsumptionDialog"
    />
  </div>
</template>

<script>
  import dayjs from 'dayjs'
  import { mapActions, mapState } from "vuex";

  import AppCard from '@/common/AppCard.vue'
  import AppInfoTooltip from '@/common/AppInfoTooltip.vue'
  import { DATA_VISUALISATION_COLORS } from '@/common/utils/colors'
  import { ISOToDDMMYYYY } from '@/common/utils/dates'
  import { SHARE_SUBSCRIPTION_CONSUMPTION_PACK } from "@/store/modules/subscription/action_types";
  import ShareSubscriptionConsumptionDialog from "@/subscriptions/views/ShareSubscriptionConsumptionDialog.vue";

  export default {
    name: 'SubscriptionConsumptionPacksTemplate',
    components: { ShareSubscriptionConsumptionDialog, AppCard, AppInfoTooltip },
    props: {
      pack: {
        type: Object,
        required: true,
      },
      isSignaturePack: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        ISOToDDMMYYYY,
        DATA_VISUALISATION_COLORS,
        shareSubscriptionConsumptionDialogIsOpen: false,
      }
    },
    computed: {
      ...mapState('subscription', ['shareSubscriptionConsumptionPackPending']),
      packTitle () {
        const type = this.isExpiredPack ? 'mixed' : this.pack.type;
        const packCategory = this.isSignaturePack ? 'Signature' : 'Envelope';
        return this.$t(`subscriptions.views.SubscriptionConsumption.Packs.${type}${packCategory}Pack`);
      },
      shareSubscriptionConsumptionDialogTitle() {
        return this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.title', { itemName: this.packTitle })
      },
      packDuration() {
        if (!this.pack.from || !this.pack.to) return {}
        const start = dayjs(this.pack.from);
        const end = dayjs(this.pack.to);

        const years = end.diff(start, 'year');
        const months = end.diff(start.add(years, 'year'), 'month');

        return {
          years,
          months,
        }
      },
      isExpiredPack () {
        return this.pack.type === 'expired'
      },
      progressBarValue () {
        return (this.pack.consumed / this.pack.total) * 100
      },
    },
    methods: {
      ...mapActions('subscription', [SHARE_SUBSCRIPTION_CONSUMPTION_PACK]),
      openShareSubscriptionConsumptionDialog () {
        this.shareSubscriptionConsumptionDialogIsOpen = true
      },
      closeShareSubscriptionConsumptionDialog () {
        this.shareSubscriptionConsumptionDialogIsOpen = false
      },
    },
  }
</script>

<style lang="scss" scoped>
:deep(.v-progress-linear) {
  border-radius: 4px !important;
  overflow: hidden;
}
</style>